import React, { useState } from 'react';
import { Plus, X, Trash2 } from 'lucide-react';

const inputStyle = { background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: '#F8FAF9' };
const cardStyle = { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)' };

const SHOE_SIZES = ['33','34','35','36','37','38','39','40','41','42','43','44','45','46','47','48'];

/**
 * Admin component for managing product variants (size, color, material, etc.).
 * Unlimited groups; each group has options with stock + price_adjust or price_override.
 *
 * Props:
 *   value: array of { name, required, options:[{value, stock, price_adjust, price_override, sku}] }
 *   onChange: (next) => void
 */
export const ProductVariantManager = ({ value = [], onChange }) => {
  const [showSizePreset, setShowSizePreset] = useState(null); // groupIdx when picking shoe sizes

  const update = (next) => onChange(next);

  const addGroup = () => update([...value, { name: '', required: true, options: [] }]);
  const removeGroup = (idx) => update(value.filter((_, i) => i !== idx));
  const renameGroup = (idx, name) => {
    const next = [...value];
    next[idx] = { ...next[idx], name };
    update(next);
  };
  const setGroupRequired = (idx, required) => {
    const next = [...value];
    next[idx] = { ...next[idx], required };
    update(next);
  };

  const addOption = (gIdx, optValue = '') => {
    const next = [...value];
    next[gIdx] = {
      ...next[gIdx],
      options: [...(next[gIdx].options || []), { value: optValue, stock: 0, price_adjust: 0, price_override: '', sku: '' }],
    };
    update(next);
  };

  const updateOption = (gIdx, oIdx, field, val) => {
    const next = [...value];
    const opts = [...next[gIdx].options];
    opts[oIdx] = { ...opts[oIdx], [field]: val };
    next[gIdx] = { ...next[gIdx], options: opts };
    update(next);
  };

  const removeOption = (gIdx, oIdx) => {
    const next = [...value];
    next[gIdx] = { ...next[gIdx], options: next[gIdx].options.filter((_, i) => i !== oIdx) };
    update(next);
  };

  const applyShoePreset = (gIdx) => {
    const next = [...value];
    const existing = (next[gIdx].options || []).map((o) => String(o.value));
    const toAdd = SHOE_SIZES.filter((s) => !existing.includes(s)).map((s) => ({
      value: s, stock: 0, price_adjust: 0, price_override: '', sku: '',
    }));
    next[gIdx] = {
      ...next[gIdx],
      name: next[gIdx].name || 'Size',
      options: [...(next[gIdx].options || []), ...toAdd],
    };
    update(next);
    setShowSizePreset(null);
  };

  return (
    <div className="space-y-4" data-testid="variant-manager">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium" style={{ color: '#F8FAF9' }}>Variants (opsional)</p>
          <p className="text-xs" style={{ color: '#A3B8AC' }}>
            Tambah grup varian seperti Size, Color, Material, Model, Capacity. Tiap opsi punya stok & adjust harga sendiri.
          </p>
        </div>
        <button
          type="button"
          onClick={addGroup}
          className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs"
          style={{ background: '#D4AF37', color: '#080C0A' }}
          data-testid="variant-add-group-btn"
        >
          <Plus className="w-3 h-3" /> Group
        </button>
      </div>

      {(value || []).map((grp, gIdx) => (
        <div key={gIdx} className="rounded-xl p-4 space-y-3" style={cardStyle} data-testid={`variant-group-${gIdx}`}>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={grp.name || ''}
              onChange={(e) => renameGroup(gIdx, e.target.value)}
              placeholder="Group name (Size / Color / Material / Model / Capacity)"
              className="flex-1 rounded-lg px-3 py-2 outline-none text-sm"
              style={inputStyle}
              data-testid={`variant-group-name-${gIdx}`}
            />
            <label className="flex items-center gap-1.5 text-xs" style={{ color: '#A3B8AC' }}>
              <input
                type="checkbox"
                checked={grp.required !== false}
                onChange={(e) => setGroupRequired(gIdx, e.target.checked)}
                data-testid={`variant-group-required-${gIdx}`}
              /> Required
            </label>
            <button
              type="button"
              onClick={() => removeGroup(gIdx)}
              className="p-2 rounded-lg"
              style={{ background: 'rgba(239,68,68,0.2)', color: '#EF4444' }}
              data-testid={`variant-remove-group-${gIdx}`}
              aria-label="Remove group"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Shoe size preset shortcut */}
          {(grp.name || '').toLowerCase().includes('size') && (
            <div>
              {showSizePreset === gIdx ? (
                <div className="flex gap-2 items-center">
                  <button
                    type="button"
                    onClick={() => applyShoePreset(gIdx)}
                    className="px-3 py-1 rounded-full text-xs"
                    style={{ background: 'rgba(212,175,55,0.18)', color: '#D4AF37', border: '1px solid rgba(212,175,55,0.4)' }}
                    data-testid={`variant-apply-shoe-preset-${gIdx}`}
                  >
                    Apply 33–48 shoe sizes
                  </button>
                  <button type="button" onClick={() => setShowSizePreset(null)} className="text-xs" style={{ color: '#A3B8AC' }}>Cancel</button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowSizePreset(gIdx)}
                  className="text-xs underline"
                  style={{ color: '#D4AF37' }}
                  data-testid={`variant-show-shoe-preset-${gIdx}`}
                >
                  Preset sepatu (size 33–48)
                </button>
              )}
            </div>
          )}

          {/* Options */}
          <div className="space-y-2">
            {(grp.options || []).map((opt, oIdx) => (
              <div key={oIdx} className="grid grid-cols-12 gap-2 items-center" data-testid={`variant-option-${gIdx}-${oIdx}`}>
                <input
                  type="text"
                  placeholder="Value (e.g. 42 / Red / Cotton)"
                  value={opt.value || ''}
                  onChange={(e) => updateOption(gIdx, oIdx, 'value', e.target.value)}
                  className="col-span-3 rounded-lg px-2 py-1.5 outline-none text-xs"
                  style={inputStyle}
                  data-testid={`variant-opt-value-${gIdx}-${oIdx}`}
                />
                <input
                  type="number"
                  min="0"
                  placeholder="Stock"
                  value={opt.stock ?? 0}
                  onChange={(e) => updateOption(gIdx, oIdx, 'stock', parseInt(e.target.value) || 0)}
                  className="col-span-2 rounded-lg px-2 py-1.5 outline-none text-xs"
                  style={inputStyle}
                  data-testid={`variant-opt-stock-${gIdx}-${oIdx}`}
                />
                <input
                  type="number"
                  placeholder="+/- Adjust"
                  value={opt.price_adjust ?? 0}
                  onChange={(e) => updateOption(gIdx, oIdx, 'price_adjust', parseInt(e.target.value) || 0)}
                  className="col-span-2 rounded-lg px-2 py-1.5 outline-none text-xs"
                  style={inputStyle}
                  data-testid={`variant-opt-adjust-${gIdx}-${oIdx}`}
                  title="Delta dari harga dasar produk (boleh negatif)"
                />
                <input
                  type="number"
                  min="0"
                  placeholder="Override Price"
                  value={opt.price_override ?? ''}
                  onChange={(e) => updateOption(gIdx, oIdx, 'price_override', e.target.value === '' ? '' : parseInt(e.target.value))}
                  className="col-span-2 rounded-lg px-2 py-1.5 outline-none text-xs"
                  style={inputStyle}
                  data-testid={`variant-opt-override-${gIdx}-${oIdx}`}
                  title="Harga absolut menggantikan base + adjust (kosongkan jika pakai adjust)"
                />
                <input
                  type="text"
                  placeholder="SKU (optional)"
                  value={opt.sku || ''}
                  onChange={(e) => updateOption(gIdx, oIdx, 'sku', e.target.value)}
                  className="col-span-2 rounded-lg px-2 py-1.5 outline-none text-xs"
                  style={inputStyle}
                  data-testid={`variant-opt-sku-${gIdx}-${oIdx}`}
                />
                <button
                  type="button"
                  onClick={() => removeOption(gIdx, oIdx)}
                  className="col-span-1 p-1.5 rounded-lg flex justify-center"
                  style={{ background: 'rgba(239,68,68,0.2)', color: '#EF4444' }}
                  data-testid={`variant-remove-opt-${gIdx}-${oIdx}`}
                  aria-label="Remove option"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => addOption(gIdx)}
              className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-full"
              style={{ background: 'rgba(255,255,255,0.05)', color: '#F8FAF9', border: '1px solid rgba(255,255,255,0.1)' }}
              data-testid={`variant-add-opt-${gIdx}`}
            >
              <Plus className="w-3 h-3" /> Add Option
            </button>
          </div>
        </div>
      ))}

      {(!value || value.length === 0) && (
        <p className="text-xs" style={{ color: '#A3B8AC' }}>Belum ada varian. Klik "+ Group" untuk menambahkan.</p>
      )}
    </div>
  );
};
